/**
 * TenantGeofenceModePanel — tenant-level geofence mode config (Phase 8 / Task 8.2).
 *
 * §15 hierarchy: DISABLED | ENABLED_VERIFIED_ONLY (default) | ENABLED_ALL.
 * The <select> is editable for DISPATCH_MANAGER+ only (UX gate); the server PATCH
 * is the authoritative guard (a plain DISPATCHER PATCH returns 403). Reads are
 * allowed for everyone.
 */

import * as React from 'react';
import { useCallback } from 'react';
import type { TenantGeofenceMode } from '@lodex-pro/shared';
import { useRoleCode } from '../../lib/use-role-code.js';
import { useTenantSettings, useSaveTenantSettings } from './use-tenant-settings.js';
import { GEOFENCE_MODE_ROLES } from '../../lib/role-gates.js';

const MODE_OPTIONS: ReadonlyArray<{ value: TenantGeofenceMode; label: string }> = [
  // OPEN-220a / DEC-386 — the mode says which sites have geofences. Whether one may MARK
  // ARRIVAL is Settings > Drivers & shifts > Arrivals (off by default).
  { value: 'DISABLED', label: 'Off — no site geofences' },
  { value: 'ENABLED_VERIFIED_ONLY', label: 'Verified sites only (default)' },
  { value: 'ENABLED_ALL', label: 'All sites with a geofence' },
];

export function TenantGeofenceModePanel(): React.JSX.Element {
  const roleCode = useRoleCode();
  const canEdit = roleCode !== null && GEOFENCE_MODE_ROLES.has(roleCode);
  const { data, isLoading } = useTenantSettings();
  const { mutate: saveSettings, isPending } = useSaveTenantSettings();

  const mode = data?.settings.geofenceMode ?? 'ENABLED_VERIFIED_ONLY';
  const currentLabel = MODE_OPTIONS.find((o) => o.value === mode)?.label ?? mode;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>): void => {
      saveSettings({ geofenceMode: e.target.value as TenantGeofenceMode });
    },
    [saveSettings],
  );

  return (
    <div
      data-testid="tenant-geofence-mode-panel"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 16px',
        borderBottom: '1px solid var(--color-border-primary)',
        backgroundColor: 'var(--color-bg-secondary)',
        fontFamily: 'var(--font-sans, DM Sans, system-ui, sans-serif)',
      }}
    >
      <label
        htmlFor={canEdit ? 'tenant-geofence-mode' : undefined}
        style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-primary)' }}
      >
        Site geofences
      </label>
      {canEdit ? (
        <select
          id="tenant-geofence-mode"
          aria-label="Tenant geofence mode"
          value={mode}
          disabled={isLoading || isPending}
          onChange={handleChange}
          style={{
            fontSize: '13px',
            padding: '4px 8px',
            borderRadius: '4px',
            border: '1px solid var(--color-border-primary)',
            backgroundColor: 'var(--color-bg-primary)',
            color: 'var(--color-text-primary)',
          }}
        >
          {MODE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : (
        // DISPATCHER and below — read-only PLAIN TEXT (no <select>, no arrow, not
        // clickable). The server PATCH is the authoritative guard regardless.
        <>
          <span
            data-testid="tenant-geofence-mode-readonly"
            style={{ fontSize: '13px', color: 'var(--color-text-primary)' }}
          >
            {currentLabel}
          </span>
          <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
            Dispatch Manager role required to change
          </span>
        </>
      )}
    </div>
  );
}
